/**
 * "Razgovor" drawer — reads the mirrored Savetnik signup and chat session
 * and renders the transcript so an operator can read the conversation
 * before approving or rejecting the lead.
 *
 * Two-hop read (signup -> chat_session_id -> session), both RLS-gated,
 * org-scoped mirror queries via useSavetnikTranscript; no new HTTP endpoint.
 */

import { MessageSquare } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { useSavetnikTranscript } from '@/hooks/crm/useSavetnikTranscript';
import {
  isOperatorRole,
  parseTranscriptMessages,
  type TranscriptEntry,
} from '@/lib/crm/savetnik-transcript';
import { cn } from '@/lib/utils';

interface SavetnikTranscriptDrawerProps {
  sourceRef: string;
  onClose: () => void;
}

const formatTimestamp = (value: string | null): string => {
  if (!value) return 'Unknown time';
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return 'Unknown time';
  return date.toLocaleString();
};

function TranscriptBubble({ entry }: { entry: TranscriptEntry }): JSX.Element {
  if (entry.kind === 'unknown') {
    return (
      <pre
        className="max-w-full overflow-x-auto whitespace-pre-wrap break-words rounded-md bg-muted p-2 text-xs text-muted-foreground"
        data-content-origin="external"
      >
        {JSON.stringify(entry.raw, null, 2)}
      </pre>
    );
  }

  const fromSavetnik = isOperatorRole(entry.role);

  return (
    <div
      className={cn(
        'max-w-[85%] break-words rounded-lg px-3 py-2 text-sm',
        fromSavetnik ? 'mr-auto bg-secondary text-secondary-foreground' : 'ml-auto bg-primary/10 text-foreground',
      )}
      data-content-origin="external"
    >
      <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">{entry.role}</p>
      {entry.text}
    </div>
  );
}

export function SavetnikTranscriptDrawer({ sourceRef, onClose }: SavetnikTranscriptDrawerProps): JSX.Element {
  const transcript = useSavetnikTranscript(sourceRef, { enabled: true });

  const signup = transcript.data?.signup ?? null;
  const session = transcript.data?.session ?? null;
  const messages = session ? parseTranscriptMessages(session.messages) : [];

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="flex w-full flex-col overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" aria-hidden="true" />
            Razgovor
          </SheetTitle>
          <SheetDescription>Savetnik chat transcript for this lead.</SheetDescription>
        </SheetHeader>

        <div className="mt-4 space-y-4">
          {transcript.isLoading ? (
            <div className="space-y-2" role="status" aria-live="polite" aria-busy="true">
              <span className="sr-only">Loading the transcript</span>
              {[1, 2, 3].map((item) => (
                <Skeleton key={item} className="h-12 w-full" />
              ))}
            </div>
          ) : transcript.isError ? (
            <div className="p-6 text-center" role="alert" aria-live="assertive">
              <p className="font-medium">The transcript could not be loaded</p>
              <p className="mt-1 text-sm text-muted-foreground">Try closing and reopening this panel.</p>
            </div>
          ) : !signup || !session ? (
            <div className="p-6 text-center" role="status" aria-live="polite">
              <p className="font-medium">Transkript nije dostupan</p>
              {signup && (
                <p className="mt-1 text-sm text-muted-foreground" data-content-origin="external">
                  {[signup.surface, signup.course_slug, signup.desired_handoff]
                    .filter(Boolean)
                    .join(' · ') || 'No session is linked to this lead.'}
                </p>
              )}
            </div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2">
                {session.surface && <Badge variant="outline">{session.surface}</Badge>}
                {signup.course_slug && <Badge variant="outline">{signup.course_slug}</Badge>}
                {signup.desired_handoff && <Badge variant="outline">{signup.desired_handoff}</Badge>}
                {session.qualification_score !== null && (
                  <Badge variant="outline">Score {session.qualification_score}</Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground">{formatTimestamp(session.created_at)}</p>

              <div className="space-y-3">
                {messages.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No messages recorded in this transcript.</p>
                ) : (
                  messages.map((entry, index) => <TranscriptBubble key={index} entry={entry} />)
                )}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
